import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

// Sostituisce window.alert / window.confirm con popup tematizzati che
// matchano il look-and-feel del gioco (cornice neon, sfondo scuro). Promise-based:
//   const ok = await dialog.confirm({ message: 'Cancellare?', variant: 'danger' });
//   await dialog.alert('Operazione completata');
//
// Keyboard: Enter = OK, Escape = Cancel (su confirm) o close (su alert).
// Solo un dialog attivo alla volta — chiamate sovrapposte vanno serializzate
// dal caller (di solito non succede perché ognuna è awaitata).

export type DialogVariant = 'info' | 'warning' | 'danger' | 'error';

export interface AlertOptions {
  title?: string;
  message: string;
  okLabel?: string;
  variant?: DialogVariant;
}

export interface ConfirmOptions extends AlertOptions {
  cancelLabel?: string;
}

interface DialogContextValue {
  alert(opts: AlertOptions | string): Promise<void>;
  confirm(opts: ConfirmOptions | string): Promise<boolean>;
}

const DialogContext = createContext<DialogContextValue | null>(null);

export function useDialog(): DialogContextValue {
  const ctx = useContext(DialogContext);
  if (!ctx) throw new Error('useDialog deve essere usato dentro <DialogProvider>');
  return ctx;
}

interface ActiveDialog {
  kind: 'alert' | 'confirm';
  title?: string;
  message: string;
  okLabel: string;
  cancelLabel?: string;
  variant: DialogVariant;
  resolve: (v: any) => void;
}

export function DialogProvider({ children }: { children: ReactNode }) {
  const [active, setActive] = useState<ActiveDialog | null>(null);

  const alert = useCallback((opts: AlertOptions | string): Promise<void> => {
    return new Promise<void>((resolve) => {
      const o = typeof opts === 'string' ? { message: opts } : opts;
      setActive({
        kind: 'alert',
        title: o.title,
        message: o.message,
        okLabel: o.okLabel ?? 'OK',
        variant: o.variant ?? 'info',
        resolve,
      });
    });
  }, []);

  const confirm = useCallback((opts: ConfirmOptions | string): Promise<boolean> => {
    return new Promise<boolean>((resolve) => {
      const o = typeof opts === 'string' ? { message: opts } : opts;
      setActive({
        kind: 'confirm',
        title: o.title,
        message: o.message,
        okLabel: o.okLabel ?? 'Confirm',
        cancelLabel: o.cancelLabel ?? 'Cancel',
        variant: o.variant ?? 'warning',
        resolve,
      });
    });
  }, []);

  const close = useCallback((value: any) => {
    if (!active) return;
    active.resolve(value);
    setActive(null);
  }, [active]);

  const handleOk = useCallback(() => {
    close(active?.kind === 'confirm' ? true : undefined);
  }, [active, close]);

  const handleCancel = useCallback(() => {
    close(active?.kind === 'confirm' ? false : undefined);
  }, [active, close]);

  // Keyboard shortcuts: Enter = OK, Escape = Cancel/Close.
  useEffect(() => {
    if (!active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        handleCancel();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleOk();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [active, handleOk, handleCancel]);

  return (
    <DialogContext.Provider value={{ alert, confirm }}>
      {children}
      {active && (
        <ThemedDialog
          dialog={active}
          onOk={handleOk}
          onCancel={handleCancel}
        />
      )}
    </DialogContext.Provider>
  );
}

interface ThemedDialogProps {
  dialog: ActiveDialog;
  onOk: () => void;
  onCancel: () => void;
}

function ThemedDialog({ dialog, onOk, onCancel }: ThemedDialogProps) {
  const { borderColor, accentColor, icon } = variantTheme(dialog.variant);
  const isAlert = dialog.kind === 'alert';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.7)',
        zIndex: 9999, // sopra tutto, anche gli altri overlay
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
      onClick={(e) => {
        // Click sul backdrop = cancel/close.
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          background: '#0e0e16',
          border: `2px solid ${borderColor}`,
          borderRadius: 10,
          padding: 24,
          maxWidth: 460,
          width: '100%',
          boxShadow: `0 0 24px ${borderColor}66`,
          fontFamily: 'inherit',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <span style={{ fontSize: 28, lineHeight: 1 }}>{icon}</span>
          {dialog.title && (
            <h2 style={{ color: accentColor, margin: 0, fontSize: 18 }}>{dialog.title}</h2>
          )}
        </div>
        <p style={{ color: '#ddd', fontSize: 14, lineHeight: 1.5, margin: '0 0 24px 0', whiteSpace: 'pre-wrap' }}>
          {dialog.message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {!isAlert && (
            <button
              onClick={onCancel}
              style={{
                background: '#333',
                border: '1px solid #444',
                color: '#bbb',
                padding: '8px 18px',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'inherit',
                fontSize: 13,
              }}
            >{dialog.cancelLabel}</button>
          )}
          <button
            onClick={onOk}
            autoFocus
            style={{
              background: accentColor,
              border: 'none',
              color: dialog.variant === 'danger' || dialog.variant === 'error' ? '#fff' : '#000',
              padding: '8px 18px',
              borderRadius: 6,
              cursor: 'pointer',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 'bold',
            }}
          >{dialog.okLabel}</button>
        </div>
      </div>
    </div>
  );
}

function variantTheme(variant: DialogVariant): { borderColor: string; accentColor: string; icon: string } {
  switch (variant) {
    case 'danger':
      return { borderColor: '#e57373', accentColor: '#e57373', icon: '⚠' };
    case 'error':
      return { borderColor: '#e57373', accentColor: '#e57373', icon: '✗' };
    case 'warning':
      return { borderColor: '#ffb703', accentColor: '#ffb703', icon: '⚠' };
    case 'info':
    default:
      return { borderColor: 'var(--accent-color)', accentColor: 'var(--accent-color)', icon: 'ℹ' };
  }
}
